import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ADRsService } from '@core/api/generated';

/**
 * Guards the `adr/:adrId` route: the adr must exist in the
 * currently selected project/branch. If it does not exist (e.g. because the
 * user switched to a branch where the adr was not yet created, or the
 * adr ID is simply invalid), the guard redirects to the ADRs
 * overview (`adr`) instead of letting the detail page render an error.
 *
 * `projectId` and `branchName` are parameters of the parent route
 * (`projects/:projectId/:branchName`). Because `paramsInheritanceStrategy`
 * is set to `'always'` (see app.config.ts), `route.paramMap` already
 * contains the inherited parent params, so no manual `route.parent`
 * traversal is necessary.
 *
 * This guard should be applied as a `canActivate` guard on the
 * `adr/:adrId` route, complementing `projectExistsGuard` and
 * `branchExistsGuard`, which already validated `projectId`/`branchName`.
 */
export const adrExistsGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const adrService = inject(ADRsService);

  const projectId = route.paramMap.get('projectId');
  const branchName = route.queryParamMap.get('branchName');
  const adrId = route.paramMap.get('adrId');

  // If any required param is missing, let other guards/logic handle it
  // (should practically not happen, since parent guards already ensure
  // projectId/branchName, and the route itself declares :adrId).
  if (!projectId || !branchName || !adrId) {
    return true;
  }

  return adrService.getAdrSummary(projectId, branchName, adrId).pipe(
    map(() => true),
    catchError(() =>
      of(
        router.createUrlTree(['/projects', projectId, 'architecture'], {
          queryParams: { branchName },
        })
      )
    )
  );
};
